import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function MailEditorSettingsCard({ authToken }) {
  const { toast } = useToast();
  const [colors, setColors] = useState(["#000000", "#1f2937", "#0f766e", "#2563eb", "#b91c1c"]);
  const [fonts, setFonts] = useState([
    'system-ui, -apple-system, "Segoe UI", sans-serif',
    "Arial, sans-serif",
    "Georgia, serif",
    "Tahoma, sans-serif",
    "Verdana, sans-serif",
  ]);
  const [sizes, setSizes] = useState({ standard: 14, h1: 26, h2: 22, h3: 18 });
  const [styles, setStyles] = useState({ standard: "normal", h1: "bold", h2: "bold", h3: "bold" });
  const [linkColor, setLinkColor] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  function authHeaders() {
    return authToken
      ? {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      : {};
  }

  useEffect(() => {
    if (!authToken) return;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/api/mail-editor-settings`, authHeaders());
        if (Array.isArray(res.data?.colors) && res.data.colors.length > 0) {
          setColors(res.data.colors.slice(0, 5));
        }
        if (Array.isArray(res.data?.fonts) && res.data.fonts.length > 0) {
          setFonts(res.data.fonts.slice(0, 5));
        }
        if (res.data?.sizes) {
          setSizes(res.data.sizes);
        }
        if (res.data?.styles) {
          setStyles(res.data.styles);
        }
        if (typeof res.data?.link_color === "string") {
          setLinkColor(res.data.link_color);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  async function handleSave() {
    if (!authToken) return;
    setSaving(true);
    try {
      await axios.post(
        `${API_BASE}/api/mail-editor-settings`,
        {
          colors,
          fonts,
          sizes,
          styles,
          link_color: linkColor || null,
        },
        authHeaders(),
      );
      toast({ title: "Farben gespeichert" });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Farben konnten nicht gespeichert werden.";
      toast({ title: "Fehler", description: msg, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="border-slate-200 bg-white/90" data-testid="mail-editor-settings-card">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-slate-900">Mail-Editor-Farben (nur Super-Admin)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-slate-500">
          Lege hier bis zu 5 erlaubte Schriftfarben, bis zu 5 Schriftarten sowie die Größen und Grundstile für Standard,
          H1, H2 und H3 fest. Andere Benutzer können nur innerhalb dieser Vorgaben arbeiten.
        </p>
        <p className="text-xs text-slate-500">
          Zusätzlich kannst du eine Standard-Linkfarbe definieren. Links in E-Mails, Vorlagen und Signaturen werden dann
          automatisch in dieser Farbe dargestellt.
        </p>
        <div className="space-y-4">
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Farben</p>
            <div className="grid gap-3 md:grid-cols-2">
              {colors.map((c, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Label className="w-10 text-xs">Farbe {idx + 1}</Label>
                  <Input
                    type="color"
                    value={c}
                    onChange={(e) => {
                      const value = e.target.value;
                      setColors((prev) => {
                        const copy = [...prev];
                        copy[idx] = value;
                        return copy;
                      });
                    }}
                    className="h-8 w-16 p-1"
                  />
                  <Input
                    value={c}
                    onChange={(e) => {
                      const value = e.target.value;
                      setColors((prev) => {
                        const copy = [...prev];
                        copy[idx] = value;
                        return copy;
                      });
                    }}
                    className="h-8 flex-1 text-xs"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Linkfarbe</p>
            <div className="flex items-center gap-2 mb-3">
              <Label className="w-20 text-xs">Linkfarbe</Label>
              <Input
                type="color"
                value={linkColor || "#0000ee"}
                onChange={(e) => setLinkColor(e.target.value)}
                className="h-8 w-16 p-1"
              />
              <Input
                value={linkColor || "#0000ee"}
                onChange={(e) => setLinkColor(e.target.value)}
                className="h-8 flex-1 text-xs"
              />
            </div>
          </div>

          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Schriftarten</p>
            <div className="space-y-2">
              {fonts.map((f, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Label className="w-14 text-xs">Font {idx + 1}</Label>
                  <Input
                    value={f}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFonts((prev) => {
                        const copy = [...prev];
                        copy[idx] = value;
                        return copy;
                      });
                    }}
                    className="h-8 flex-1 text-xs"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Größen & Stile</p>
            <div className="space-y-2 text-xs">
              {[
                { key: "standard", label: "Standard" },
                { key: "h1", label: "H1" },
                { key: "h2", label: "H2" },
                { key: "h3", label: "H3" },
              ].map(({ key, label }) => (
                <div key={key} className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.4fr)] items-center gap-2">
                  <div>{label}</div>
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] text-slate-500">Größe</span>
                    <Input
                      type="number"
                      min={8}
                      max={60}
                      value={sizes[key] ?? ""}
                      onChange={(e) => {
                        const value = parseInt(e.target.value || "0", 10) || 0;
                        setSizes((prev) => ({ ...prev, [key]: value }));
                      }}
                      className="h-8 w-20 text-xs"
                    />
                    <span className="text-[11px] text-slate-500">px</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] text-slate-500">Stil</span>
                    <select
                      value={styles[key] ?? "normal"}
                      onChange={(e) => setStyles((prev) => ({ ...prev, [key]: e.target.value }))}
                      className="h-8 flex-1 rounded-md border border-slate-300 bg-white px-2 text-[11px] text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                    >
                      <option value="normal">Normal</option>
                      <option value="bold">Fett</option>
                      <option value="italic">Kursiv</option>
                      <option value="bold_italic">Fett + Kursiv</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            className="rounded-full bg-sky-600 px-4 text-xs font-medium text-white hover:bg-sky-500 disabled:opacity-60"
            onClick={handleSave}
            disabled={saving}
            data-testid="mail-editor-settings-save"
          >
            {saving ? "Wird gespeichert..." : "Farben speichern"}
          </Button>
        </div>
        {loading && <p className="text-[11px] text-slate-400">Lade aktuelle Einstellungen...</p>}
      </CardContent>
    </Card>
  );
}
